import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';
import { getOrgConfig } from '../../utils/orgResolution';
import { apiGetPostFormBundle, apiGetReferralParticipants, apiListTeacherTasks } from '../../utils/rmsApi';
import { isTeacherFormDone } from '../../utils/teacherPupils';
import { harbourFormKeysFor, harbourFormTitle } from '../../utils/harbourForms';
import { buildFormProgressFromRecord, TRIAGE_FORM_KEYS } from '../../utils/referralStatus';
import { answerText, printableSections } from '../../utils/answerText';
import { PARTICIPANT_ROLE } from '../../../constants/participantStatuses';
import HarbourToast from '../../components/harbour/HarbourToast';
import useReleasedPageFrame from '../../hooks/useReleasedPageFrame';
import './hfpPrint.css';

/*
 * Print view of a person's own answers (TDI-53 section 30 Download, TDI-692).
 * Version one of the PDF pipeline: one printable document, the browser's
 * save-as-PDF is the output.
 *
 * - The SENCO (role school_user, /rms/pupils/:id/answers/print): every school
 *   form on the record that the SENCO holds and has submitted. A form handed
 *   to a teacher is the teacher's, so it is left out and named in a closing
 *   line; the teacher downloads their own.
 * - A parent or carer (role guardian, /rms/family/child/:id/forms/:pathway
 *   ?print=answers): their submitted answers to that page's forms only.
 * - A class teacher (role teacher, /rms/teacher/pupils/:id/answers/print,
 *   TDI-620 "Download your answers"): the forms they finished for this pupil.
 *   A teacher cannot read the record, so the forms and the pupil's name come
 *   from their own task list; the server serves only the school instances
 *   allocated to them.
 *
 * Each person only ever receives their own role's instances: the bundles are
 * the ones the review screens read, and the server enforces the role.
 */

/** The answer for one question, as plain printable text (kept for callers). */
export function formatAnswer(question, value) {
  return answerText(question, { [question.questionId]: value });
}

const isSubmitted = (formProgress, instanceId) => Number(formProgress[instanceId]) >= 100;

/** "A, B and C" */
function listInWords(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The form keys this person answered on this record. */
function formKeysFor(role, pathway, record) {
  const senActive = (record.senPathway || {}).status === 'active';
  const opts = {
    patient: record,
    intakeConcern: record.intakeConcern || null,
    senActive,
    schoolPhase: record.schoolPhase || null,
  };
  if (role === 'guardian') {
    // The page's own forms, as the parent or carer's forms homepage lists
    // them: the seven SEND forms, or the referral forms and the seven
    // history forms.
    return pathway === 'sen'
      ? harbourFormKeysFor('sen', opts)
      : [...harbourFormKeysFor('referral', opts), ...TRIAGE_FORM_KEYS];
  }
  const referralActive = record.intakeConcern || (record.referralPathway || {}).startedAt || !(record.harbour || {}).pupil;
  return Array.from(new Set([
    ...(senActive ? harbourFormKeysFor('sen', opts) : []),
    ...(referralActive ? harbourFormKeysFor('referral', opts) : []),
  ]));
}

/** Which teachers hold which school forms (TDI-489 allocations). A teacher
 * who no longer works at the school (TDI-617) cannot download anything, so
 * the forms they finished come back into the SENCO's own download. */
async function teacherHolders(orgId, patientRecordId) {
  const { participants } = await apiGetReferralParticipants({ orgId, patientRecordId });
  const teachers = (Array.isArray(participants) ? participants : []).filter(
    (p) => p.role === PARTICIPANT_ROLE.TEACHER
      && !['superseded', 'removed', 'disabled', 'archived', 'left'].includes(p.status)
  );
  const holders = new Map();
  teachers.forEach((teacher) => {
    Object.entries(teacher.allocations || {}).forEach(([formKey, allocated]) => {
      if (!allocated) return;
      holders.set(formKey, [...(holders.get(formKey) || []), teacher]);
    });
  });
  return holders;
}

/** The teacher's finished forms for one pupil, from their own task list. */
async function teacherScope(orgId, patientRecordId) {
  const tasks = await apiListTeacherTasks({ orgId });
  const child = ((tasks && tasks.children) || []).find((c) => c.patientRecordId === patientRecordId);
  if (!child) return { keys: [], childName: '' };
  return {
    keys: (child.forms || []).filter(isTeacherFormDone).map((f) => f.formKey),
    childName: child.pupilFirstName || String(child.pupilName || '').split(' ')[0] || '',
  };
}

export function AnswersPrintView({ role, pathway = null }) {
  // TDI-794: without this, the saved PDF held one page — the first
  // screenful — and every later section was silently missing.
  useReleasedPageFrame();
  const { patientRecordId } = useParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId || (role === 'guardian' ? getOrgConfig().orgId : null);

  const [forms, setForms] = useState(null); // [{ formKey, title, sections }]
  const [heldByTeachers, setHeldByTeachers] = useState([]); // ["Dana filled in X. They can ..."]
  const [childName, setChildName] = useState('');
  const [error, setError] = useState(null);
  const [printed, setPrinted] = useState(false);

  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // A teacher fills in school forms, so their instances are the
        // school's ('_school_user'), and nothing reads the record.
        const instanceRole = role === 'teacher' ? 'school_user' : role;
        let keys;
        const closing = [];
        if (role === 'teacher') {
          const scope = await teacherScope(orgId, patientRecordId);
          keys = scope.keys;
          if (!cancelled) setChildName(scope.childName);
        } else {
          const snap = await getDoc(doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId));
          const record = snap.exists() ? snap.data() || {} : {};
          if (!cancelled) {
            setChildName(record?.subject?.firstName || record?.subject_first_name || '');
          }
          const { formProgress } = buildFormProgressFromRecord(record);
          keys = formKeysFor(role, pathway, record);

          // The SENCO's download leaves out every form a teacher holds, even
          // one ticked for two teachers. If the holders cannot be read, nothing
          // prints: guessing would hand a teacher's answers to the SENCO.
          if (role === 'school_user') {
            const holders = await teacherHolders(orgId, patientRecordId);
            const byTeacher = new Map();
            keys.filter((k) => holders.has(k)).forEach((formKey) => {
              holders.get(formKey).forEach((teacher) => {
                const id = teacher.participantId || teacher.id || teacher.email;
                const entry = byTeacher.get(id) || { teacher, formKeys: [] };
                entry.formKeys.push(formKey);
                byTeacher.set(id, entry);
              });
            });
            byTeacher.forEach(({ teacher, formKeys }) => {
              const name = teacher.firstName || [teacher.firstName, teacher.lastName].filter(Boolean).join(' ') || 'A teacher';
              closing.push(`${name} filled in ${listInWords(formKeys.map((k) => harbourFormTitle(k)))}. They can download their own answers.`);
            });
            keys = keys.filter((k) => !holders.has(k));
          }

          // Submitted forms only: a draft is not an answer anyone has given yet.
          keys = keys.filter((k) => isSubmitted(formProgress, `${k}_${role}`));
        }

        const bundles = await Promise.all(keys.map(async (formKey) => {
          try {
            const bundle = await apiGetPostFormBundle({
              orgId,
              patientRecordId,
              formKey,
              instanceId: `${formKey}_${instanceRole}`,
            });
            return { formKey, bundle };
          } catch (_e) {
            return { formKey, bundle: null };
          }
        }));
        const built = bundles
          .filter(({ bundle }) => bundle && bundle.schema && bundle.answers && Object.keys(bundle.answers).length > 0)
          .map(({ formKey, bundle }) => ({
            formKey,
            title: harbourFormTitle(formKey, bundle.schema),
            // Only what this person was asked, exactly as the form showed it.
            sections: printableSections(bundle, instanceRole),
          }));
        if (!cancelled) {
          setHeldByTeachers(closing);
          setForms(built);
        }
      } catch (_e) {
        if (!cancelled) setError('Your answers could not be loaded. Close this tab and try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, patientRecordId, role, pathway]);

  useEffect(() => {
    if (!forms) return undefined;
    const onAfterPrint = () => setPrinted(true);
    window.addEventListener('afterprint', onAfterPrint);
    const timer = setTimeout(() => window.print(), 400);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', onAfterPrint);
    };
  }, [forms]);

  if (error) return <p className="hfp-print__status" role="alert">{error}</p>;
  if (!forms) return <p className="hfp-print__status" role="status">Preparing your answers for download…</p>;

  let title;
  if (role === 'guardian') {
    const which = pathway === 'sen' ? 'SEND' : 'referral';
    title = childName ? `Your answers to ${childName}'s ${which} forms` : `Your answers to the ${which} forms`;
  } else {
    title = childName ? `Your answers to the school forms about ${childName}` : 'Your answers to the school forms';
  }
  const who = childName || 'this child';

  return (
    <div className="hfp-print">
      <header className="hfp-print__head">
        <h1>{title}</h1>
      </header>
      {forms.length === 0 && <p>No submitted forms to show yet.</p>}
      {forms.map((form) => (
        <section key={form.formKey} className="hfp-print__section">
          <h2>{form.title}</h2>
          {form.sections.map((section, si) => (
            <div key={`${form.formKey}-${si}`} className="hfp-print__area">
              {section.title && <h3>{section.title}</h3>}
              {section.questions.map((q) => (
                <div key={q.id} className="hfp-print__answer">
                  <p className="hfp-print__question">{q.label}</p>
                  <p className="hfp-print__value">{q.answer}</p>
                </div>
              ))}
            </div>
          ))}
        </section>
      ))}
      {heldByTeachers.length > 0 && (
        <footer className="hfp-print__foot">
          {heldByTeachers.map((line) => <p key={line}>{line}</p>)}
        </footer>
      )}
      {printed && (
        <HarbourToast title="Your answers are ready to save as a PDF" onClose={() => setPrinted(false)}>
          {`They contain ${who}'s personal information, so keep them safe and share them only with people who support ${who}.`}
        </HarbourToast>
      )}
    </div>
  );
}

/** The SENCO's download (route /rms/pupils/:patientRecordId/answers/print). */
export default function SchoolAnswersPrintPage() {
  return <AnswersPrintView role="school_user" />;
}

/** A teacher's download (route /rms/teacher/pupils/:patientRecordId/answers/print, TDI-620). */
export function TeacherAnswersPrintPage() {
  return <AnswersPrintView role="teacher" />;
}
